import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { ImageUploader } from "./AdminShared";

const TABS = [
  ["homepage", "Homepage"], ["about", "About"], ["contact", "Contact"], ["faqs", "FAQs"],
  ["terms", "Terms"], ["privacy", "Privacy"], ["delivery_returns", "Delivery & Returns"],
  ["cookies", "Cookie Policy"], ["accessibility", "Accessibility"],
];

const FieldText = ({ label, value, onChange, rows }) => (
  <div>
    <label className="text-xs uppercase tracking-luxe block mb-1">{label}</label>
    {rows ? <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm" />
      : <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm" />}
  </div>
);

const FieldImage = ({ label, value, onChange }) => (
  <div>
    <label className="text-xs uppercase tracking-luxe block mb-1">{label}</label>
    <ImageUploader single value={value} onChange={onChange} />
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm mt-2" />
  </div>
);

const FieldImages = ({ label, value, onChange }) => (
  <div>
    <label className="text-xs uppercase tracking-luxe block mb-1">{label}</label>
    <ImageUploader value={value || []} onChange={onChange} />
    <input value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="…or paste image URLs, comma separated" className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm mt-2" />
  </div>
);

export default function AdminContent() {
  const [tab, setTab] = useState("homepage");
  const [data, setData] = useState({});
  useEffect(() => { api.get(`/content/${tab}`).then((r) => setData(r.data?.data || {})); }, [tab]);
  const set = (k, v) => setData((s) => ({ ...s, [k]: v }));

  const save = async () => {
    try { await api.put(`/content/${tab}`, data); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="font-serif-display text-4xl mb-6">Content & Pages</h1>
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-sm text-sm ${tab === k ? "bg-brand-ink text-white" : "bg-white border border-brand-border hover:bg-brand-blush/40"}`} data-testid={`content-tab-${k}`}>{label}</button>
        ))}
      </div>

      <div className="bg-white border border-brand-border rounded-sm p-6 space-y-4 max-w-3xl">
        {tab === "homepage" && (
          <>
            <FieldText label="Announcement bar" value={data.announcement_bar} onChange={(v) => set("announcement_bar", v)} />
            <FieldText label="Hero eyebrow (small line above the title)" value={data.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} />
            <FieldText label="Hero title" value={data.hero_title} onChange={(v) => set("hero_title", v)} />
            <FieldText label="Hero subtitle" value={data.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} rows={2} />
            <FieldImage label="Hero image" value={data.hero_image} onChange={(v) => set("hero_image", v)} />
            <FieldText label="Hero CTA text" value={data.hero_cta} onChange={(v) => set("hero_cta", v)} />
            <div className="border-t border-brand-border pt-4 mt-2">
              <FieldText label="'Find me in person' heading" value={data.events_title} onChange={(v) => set("events_title", v)} />
              <div className="mt-3"><FieldText label="'Find me in person' text — markets, fairs & pop-ups this month. Leave blank to hide this block from the homepage." value={data.events_text} onChange={(v) => set("events_text", v)} rows={4} /></div>
            </div>
            <FieldText label="Maker section title" value={data.maker_title} onChange={(v) => set("maker_title", v)} />
            <FieldText label="Maker section text" value={data.maker_text} onChange={(v) => set("maker_text", v)} rows={4} />
            <FieldImage label="Maker image" value={data.maker_image} onChange={(v) => set("maker_image", v)} />
            <FieldText label="Instagram handle" value={data.instagram_handle} onChange={(v) => set("instagram_handle", v)} />
            <FieldImages label="Instagram images" value={data.instagram_images} onChange={(v) => set("instagram_images", v)} />
            <div className="border-t border-brand-border pt-4 mt-2">
              <FieldText label="Lookbook title" value={data.lookbook_title} onChange={(v) => set("lookbook_title", v)} />
              <div className="mt-3"><FieldImages label="Lookbook photos (model shots — shown as a gallery, not clickable)" value={data.lookbook_images} onChange={(v) => set("lookbook_images", v)} /></div>
            </div>
          </>
        )}
        {tab === "about" && (<>
          <FieldText label="Title" value={data.title} onChange={(v) => set("title", v)} />
          <FieldText label="Intro" value={data.intro} onChange={(v) => set("intro", v)} rows={2} />
          <FieldText label="Body (use blank lines for paragraphs)" value={data.body} onChange={(v) => set("body", v)} rows={8} />
          <FieldImage label="Image" value={data.image} onChange={(v) => set("image", v)} />
        </>)}
        {tab === "contact" && (<>
          <FieldText label="Intro" value={data.intro} onChange={(v) => set("intro", v)} rows={2} />
          <FieldText label="Email" value={data.email} onChange={(v) => set("email", v)} />
          <FieldText label="Trading address" value={data.address} onChange={(v) => set("address", v)} rows={2} />
        </>)}
        {tab === "faqs" && <FaqEditor data={data} setData={setData} />}
        {["terms", "privacy", "delivery_returns", "cookies", "accessibility"].includes(tab) && (<>
          <FieldText label="Title" value={data.title} onChange={(v) => set("title", v)} />
          <FieldText label="Body (use blank lines for paragraphs)" value={data.body} onChange={(v) => set("body", v)} rows={16} />
        </>)}
        <button onClick={save} className="bg-brand-ink text-white px-8 py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors" data-testid="save-content">Save changes</button>
      </div>
    </div>
  );
}

function FaqEditor({ data, setData }) {
  const items = data.items || [];
  const upd = (i, k, v) => setData({ ...data, items: items.map((it, x) => x === i ? { ...it, [k]: v } : it) });
  return (
    <div className="space-y-4">
      {items.map((it, i) => (
        <div key={i} className="border border-brand-border rounded-sm p-3 space-y-2">
          <input value={it.q} onChange={(e) => upd(i, "q", e.target.value)} placeholder="Question" className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm" />
          <textarea value={it.a} onChange={(e) => upd(i, "a", e.target.value)} placeholder="Answer" rows={2} className="w-full border border-brand-border rounded-sm px-3 py-2 text-sm" />
          <button onClick={() => setData({ ...data, items: items.filter((_, x) => x !== i) })} className="text-destructive text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove</button>
        </div>
      ))}
      <button onClick={() => setData({ ...data, items: [...items, { q: "", a: "" }] })} className="text-sm underline flex items-center gap-1" data-testid="add-faq"><Plus className="w-4 h-4" /> Add FAQ</button>
    </div>
  );
}
